import { RNS3 } from '@trupropel/aws-s3-file-upload';
import React, { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { S3Credentials } from '../types';

type UploadContextType = {
  uploadFile: (filePath: string) => Promise<string>;
  maxPicturesResolution: number;
};

export const UploadContext = React.createContext(undefined as UploadContextType);

type UploadProviderProps = {
  s3BucketName: string;
  s3HostUrl?: string;
  s3Prefix?: string;
  s3Region: string;
  children: ReactNode;
  maxPicturesResolution?: number;
} & S3Credentials;

const DEFAULT_MAX_PICTURES_RESOLUTION = 16.8;

export const UploadProvider = (props: UploadProviderProps) => {
  const { s3BucketName, s3HostUrl, s3Prefix, s3Region, s3AccessKey, s3SecretKey, getS3Credentials, children } = props;

  const maxPicturesResolution = props.maxPicturesResolution ?? DEFAULT_MAX_PICTURES_RESOLUTION;

  const getHost = (): string => {
    if (!s3HostUrl) {
      // default S3 host
      return `${s3BucketName}.s3.amazonaws.com`;
    }
    // strip away leading protocol and path
    const host = /h?t?t?p?s?:?\/?\/?([^/]+?)(?=$|\/)/.exec(s3HostUrl)[1];
    if (host !== s3HostUrl) {
      console.warn(
        `Using image host "${host}" which is different than provided one (${s3HostUrl}). Please use "${host}" to clear this warning.`,
      );
    }
    return host;
  };

  const uploadFile = async (filePath: string) => {
    try {
      const fileName: string = uuidv4();
      let keyPrefix = (!!s3Prefix || s3Prefix === '' ? s3Prefix : 'public/').trim();
      if (keyPrefix[0] === '/') {
        // remove leading / if needed
        keyPrefix = keyPrefix.slice(1);
      }
      if (keyPrefix && keyPrefix[keyPrefix.length - 1] !== '/') {
        // add trailing / if needed, only for non-empty prefixes
        keyPrefix += '/';
      }
      const options: {
        keyPrefix: string;
        bucket: string;
        region: string;
        accessKey: string;
        secretKey: string;
        sessionToken?: string;
        successActionStatus: number;
      } = {
        keyPrefix,
        bucket: s3BucketName,
        region: s3Region,
        accessKey: s3AccessKey,
        secretKey: s3SecretKey,
        successActionStatus: 201,
      };

      const file = {
        uri: filePath,
        name: fileName,
        type: 'image/jpeg',
      };

      if (getS3Credentials) {
        const credentials = await getS3Credentials();
        options.accessKey = credentials.accessKey;
        options.secretKey = credentials.secretKey;
        options.sessionToken = credentials.sessionToken;
      }

      let res;
      /**
       * We check for blob urls in case we are trying to upload
       * images from a browser
       */
      if (file?.uri?.startsWith('blob:')) {
        res = await RNS3.putWeb(file, options);
      } else {
        res = await RNS3.put(file, options);
      }

      const host = getHost();
      const publicUrl = `https://${host}/${keyPrefix}${fileName}`;
      if (res.status === 201) {
        // when keyPrefix is empty, we get https://s3Host/fileName
        // when keyPrefix is not empty empty, we get https://s3Host/prefix/fileName
        console.debug({
          message: 'Uploaded picture',
          host,
          keyPrefix,
          fileName,
          publicUrl,
        });
        return publicUrl;
      } else {
        console.error({
          message: 'Error while uploading picture',
          host,
          keyPrefix,
          fileName,
          publicUrl,
          response: res.text,
        });
        throw new Error('error uploading to s3');
      }
    } catch (err) {
      console.error({
        message: 'Error while uploading picture',
        description: err.message,
      });
      throw new Error('error uploading to s3');
    }
  };

  return <UploadContext.Provider value={{ uploadFile, maxPicturesResolution }}>{children}</UploadContext.Provider>;
};

export function useUploadContext() {
  return React.useContext(UploadContext);
}
